import { CombineOperator, CreateLocationRequest, GetTopicResponse, KeywordSearch, Location, SearchFilter, TopicSearchResult, TopicSearchSort } from '../types';
import { LitElement, css, html } from 'lit';
import { STOP_WORDS, inspirationMapIds } from '../helpers/constants';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, undefinedIfEmpty } from '../helpers/utils';
import { getDeviceGeolocation, inIframe } from '../helpers/device';
import { getTopic, getTopics } from '../helpers/api/topics-api';

import { ProxiInputPlace } from '../components/common/proxi-input-place';
import { TextField } from '@material/mwc-textfield';
import { loadGoogleMapsApi } from '../helpers/api/google-api';
import { GoogleTagEvent, logWithGoogleTag } from '../helpers/api/api';
import { getNewUiUrl } from '../helpers/auth-cookie';

@customElement('discover-page')
export class DiscoverPage extends LitElement {
    static amplitude_callsite: string = 'Discover Page';



    @query('#searchText')
    private readonly searchTextElement!: TextField;

    @query('#locationFilter')
    private readonly locationFilterElement!: ProxiInputPlace;

    @property ({ type: Boolean })
    hideHeader: boolean = false;

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    search_text: string = '';

    @state()
    is_loading: boolean = false;

    @state()
    featuredMaps: TopicSearchResult[] = [];

    @state()
    trendingThisWeek: TopicSearchResult[] = [];

    @state()
    nearbyMaps: TopicSearchResult[] = [];

    @state()
    engagingMaps: TopicSearchResult[] = [];

    @state()
    userLocation?: string;

    @state()
    nearbyDistance: number = 10000;

    async firstUpdated() {
        // Discover lives in the new app now; bounce before Google Maps loads.
        window.location.replace(`${getNewUiUrl()}/discover`);
    }

    private async startup() {
        await loadGoogleMapsApi();
        window.dispatchEvent(new CustomEvent('page-content-ready'));
        await this.renderInitialTopics();
    }

    private readonly searchText = debounce(this.searchTextInternal, 500);
    private searchTextInternal() {
        this.search_text = undefinedIfEmpty(this.searchTextElement.value?.trim().toLocaleLowerCase().replace(/[^0-9a-z ]/gi, '')) ?? '';
    }

    private async renderInitialTopics() {
        try {
            const randomIds = inspirationMapIds.sort(() => Math.random() - Math.random()).slice(0, 12);
            const promises = randomIds.map(async (id) => {
                const newResults: GetTopicResponse = await getTopic('inspiration maps', id);
                this.featuredMaps.push(newResults);
                return newResults;
            });
            await Promise.all(promises);
        } catch (e) {
            console.error('Error loading initial topics', e);
        }

        try {
            this.trendingThisWeek = await this.searchForTopics(undefined, undefined, TopicSearchSort.SevenDayTopicViews);
        } catch (e) {
            console.error('Error loading trending topics', e);
        }

        try {
            const userLocation = await this.getUserLocation();
            if (userLocation !== undefined) {
                const locationFilter = {
                    search: userLocation.search,
                    place_id: userLocation.place_id,
                    latLng: userLocation.latLng
                };
                this.locationFilterElement.value = locationFilter;
                this.nearbyMaps = await this.searchForTopics(undefined, locationFilter, TopicSearchSort.ThirtyDayTopicViews);
                this.requestUpdate();
            }
        } catch (e) {
            console.error('Error loading nearby topics', e);
        }
    }

    private async searchForTopics(searchText?: string, locationFilter?: CreateLocationRequest, sort?: TopicSearchSort) {
        const searchFilter: SearchFilter = {
            num_results: 12,
        };
        if (searchText !== undefined) {
            const keywords = searchText.split(' ').filter((search_word) => !STOP_WORDS.includes(search_word) && search_word !== '');
            if (keywords.length > 0) {
                const keyword_search: KeywordSearch = {
                    operator: CombineOperator.And,
                    keywords,
                    sub_searches: []
                };
                searchFilter.keyword_search = keyword_search;
            }
        }

        if (locationFilter !== undefined && locationFilter.search !== '') {
            searchFilter.location_match = {
                location: {
                    search: locationFilter.search ?? '',
                    place_id: locationFilter.place_id ?? '',
                    latLng: {
                        lat: locationFilter.latLng?.lat ?? 0,
                        lng: locationFilter.latLng?.lng ?? 0
                    }
                },
                max_distance: this.nearbyDistance
            };
        }

        if (sort !== undefined) {
            searchFilter.sort = sort;
        }

        return (await getTopics(DiscoverPage.amplitude_callsite, searchFilter)).sort((topic) => topic.statistics.approximate_topic_thirty_day_views) ?? [];
    }

    async runSearch(searchText?: string, locationFilter?: CreateLocationRequest, sort?: TopicSearchSort) {
        this.is_loading = true;

        if (searchText) {
            logWithGoogleTag(DiscoverPage.amplitude_callsite, GoogleTagEvent.DISCOVER_SEARCH, {
                search_query: searchText,
                has_location_filter: locationFilter !== undefined,
            });
        }

        this.topics = await this.searchForTopics(searchText, locationFilter, sort);
        this.is_loading = false;
    }

    pickMaxDistance(types: string[]) {
        // Calculated using meters = ( 40000/2 ^ zl ) * 2 * 1000
        if (types.includes('neighborhood')) { // zoom level 13
            return 10000;
        } else if (types.includes('sublocality')) { // zoom level 13
            return 10000;
        } else if (types.includes('locality')) { // zoom level 11
            return 20000;
        } else if (types.includes('administrative_area_level_1')) { // zoom level 7
            return 625000;
        } else if (types.includes('country')) { // zoom level 4
            return 5000000;
        }
        return 10000; // zoom level 13
    }

    async addKeyboardEvents(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            await this.runSearch(this.search_text, this.locationFilterElement.value);
        }
    }

    async getUserLocation(): Promise<Location | undefined> {
        this.userLocation = await getDeviceGeolocation();
        if (this.userLocation !== undefined && this.userLocation !== '') {
            let dataGeocode;
            try {
                dataGeocode = await this.locationFilterElement.geocode(this.userLocation);
            } catch (e) {
                return;
            }
            if (dataGeocode.length === 0 || !dataGeocode[0].formatted_address) {
                return;
            }

            const place = {
                search: dataGeocode[0].formatted_address,
                place_id: dataGeocode[0].place_id,
                latLng: {
                    lat: dataGeocode[0].geometry.location.lat(),
                    lng: dataGeocode[0].geometry.location.lng(),
                }
            };

            this.nearbyDistance = this.pickMaxDistance(dataGeocode[0].types);
            return place;
        }
        return undefined;
    }

    makeMapClick() {
        // open create map page
        window.location.href = '#template-onboard?from=discover';
    }

    demoClick() {
        window.location.href = 'https://dnwmcqt981r.typeform.com/to/opWRjbWU?utm_source=proxi_discover&utm_medium=proxi_discover&utm_campaign=proxi_discover&utm_term=proxi_discover&utm_content=proxi_discover';
    }

    render() {
        return html`
            <div class="pageWrapper">
                ${!inIframe() && !this.hideHeader ? html`
                    <h1> Explore Proxi Maps </h1>
                    <h2 style="margin:0"> Search our public maps to get inspired </h2>
                    <div class="actionButtons">
                        <mwc-button @click="${this.makeMapClick}" raised>Make a Map</mwc-button>
                        <mwc-button @click="${this.demoClick}" raised>Watch a Demo</mwc-button>
                    </div>
                ` : ''}
                <div class="search">
                    <proxi-textfield
                        id="searchText"
                        label="Search for maps"
                        icon="search"
                        outlined
                        @input="${this.searchText}"
                        @keypress="${async (e: KeyboardEvent) => await this.addKeyboardEvents(e)}"
                    ></proxi-textfield>

                    <proxi-input-place
                        id="locationFilter"
                        class="input"
                        label="City or Area"
                        >
                    </proxi-input-place>
                    <mwc-button @click="${async () => await this.runSearch(this.search_text, this.locationFilterElement.value, TopicSearchSort.ThirtyDayTopicViews)}" raised>Search</mwc-button>
                </div>
                <div class="filterContainer">
                    ${this.topics.length > 0 ? html`
                        <h2>${this.searchTextElement.value ? `Results for "${this.searchTextElement.value ?? ''}" ${this.locationFilterElement?.value?.search ? `near ${this.locationFilterElement.value.search}` : ''}` : this.locationFilterElement?.value?.search ? `Maps near ${this.locationFilterElement.value.search}` : 'Search Results'}</h2>
                    ` : ''}
                    <div class="resultsContainer">
                        ${this.is_loading ? html`
                            <div class="loadingImage">
                                <img src="/images/backpack/ic_loading.gif" />
                            </div>
                        ` : this.topics.length > 0 ? html`
                            <div class="results">
                                ${this.topics.map((topic) => {
                                    return html`
                                    <div class="column-item">
                                        <backpack-card class="topic" .topic=${topic} ?goToDetailPage=${true} hideViewerType>
                                        </backpack-card>
                                    </div>
                                    `;
                                })}
                                ${this.topics.length === 0
                                    ? html`
                                        <div class="column-item">
                                            <p>No results found</p>
                                        </div>
                                    `
                                    : ''
                                }
                            </div>
                        ` : ''}
                    </div>
                    ${this.trendingThisWeek.length > 0 ? html`
                        <div class="resultsContainer">
                            <h2>Trending This Week</h2>
                            <div class="results">
                                ${this.trendingThisWeek.map((topic) => {
                                    return html`
                                        <div class="column-item">
                                            <backpack-card class="topic" .topic=${topic} ?goToDetailPage=${true} hideViewerType>
                                            </backpack-card>
                                        </div>
                                    `;
                                })}
                            </div>
                        </div>
                    ` : ''}
                    ${this.nearbyMaps.length > 0 ? html`
                        <div class="resultsContainer">
                            <h2>Maps Near ${this.userLocation}</h2>
                            <div class="results">
                                ${this.nearbyMaps.map((topic) => {
                                    return html`
                                        <div class="column-item">
                                            <backpack-card class="topic" .topic=${topic} ?goToDetailPage=${true} hideViewerType>
                                            </backpack-card>
                                        </div>
                                    `;
                                })}
                            </div>
                        </div>
                    ` : ''}
                    ${this.featuredMaps.length > 0 ? html`
                        <div class="resultsContainer">
                            <h2>Featured Maps</h2>
                            <div class="results">
                                ${this.featuredMaps.map((topic) => {
                                    return html`
                                        <div class="column-item">
                                            <backpack-card class="topic" .topic=${topic} ?goToDetailPage=${true} hideViewerType>
                                            </backpack-card>
                                        </div>
                                    `;
                                })}
                            </div>
                        </div>
                    ` : ''}
                </div>
                <footer>
                    <p style="margin-left: 10px;" class="hidden"><a href="https://map.proxi.co/sitemap">Sitemap</a></p>
                </footer>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            #rootContainer {
                min-height: 100vh;
                background: var(--theme-border-color);
            }

            .hidden {
                display:none;
            }

            .pageWrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
                padding: 20px;
            }

            .search {
                display: flex;
                flex-direction: row;
                gap: 10px;
                align-items: flex-start;
                justify-content: center;
                box-sizing: border-box;
                padding: 20px;
                border-radius: 6px;
                box-shadow: 0px 2px 2px -2px rgb(31 27 45 / 8%), 0px 4px 12px rgb(31 27 45 / 8%);
            }

            #googleInputDiv {
                display: flex;
                margin-bottom: 15px;
                margin-top: 10px;
                width: 100%;
                align-items: center;
            }

            #locationFilter {
                width: 100%;
            }

            .filterContainer {
                min-height: calc(100vh - 18.5rem);
                min-width: 379px;
                box-sizing: border-box;
                grid-template-columns: repeat(auto-fit, minmax(332px, 1fr));
                gap: 20px;
                width: 100%;
                padding: 40px 80px;
                margin-left: auto;
                margin-right: auto;
                overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
            }

            .filter {
                margin: 20px;
                padding: 30px;
                display: flex;
                flex-direction: column;
                align-items: center;
                height: 100%;
                border-radius: 6px;
                box-shadow: 0px 2px 2px -2px rgb(31 27 45 / 8%), 0px 4px 12px rgb(31 27 45 / 8%);
            }

            .loadingImage {
                display: flex;
                justify-content: center;
                max-height: 100px;
            }

            .resultsContainer {
                width: 100%;
            }

            .actionButtons {
                display: flex;
                flex-direction: row;
                justify-content: center;
                padding: 20px;
                gap: 20px;
            }

            .results {
                padding-top: 0.5rem;
                padding-bottom: 2rem;
                width: 100%;
                box-sizing: border-box;
                margin: 0px -10px;
                display: flex;
                flex-wrap: wrap;
                overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
            }

            .column-item {
                width: 25%;
                min-width: 150px;
                padding: 10px;
                box-sizing: border-box;
            }

            .gptRedirect {
                font-weight: bold;
                font-size: large;
            }

            #searchText {
                flex:3;
            }

            #locationFilter {
                flex: 2;
            }

            @media only screen and (max-width: 550px) {
                .filterContainer {
                    padding: 0;
                    display: flex;
                    min-width: unset;
                }

                .search {
                    flex-direction: column;
                    gap: 20px;
                    padding: 10px;
                    align-items: center;
                }

                h2 {
                    text-align: center;
                }

                #searchText {
                    width: 60vw;
                }
            }
        `;
    }
}
